/**
 * The notification bell in the top bar — one count of what is waiting on the
 * signed-in user personally, on every screen.
 * ---------------------------------------------------------------------------
 * Two signals, deliberately independent:
 *
 *   COUNT  How many things are outstanding. It comes from the server and
 *          NEVER clears because somebody looked. A task is done when it is
 *          done; a bell that zeroes on open lets people dismiss work by
 *          glancing at it.
 *   DOT    "Something new since you last opened this." Per user, in
 *          localStorage, cleared on open. It is only a hint, so losing it
 *          (private window, cleared storage) costs nothing.
 *
 * There is no mark-as-read and no dismiss, for the same reason as the count.
 * Approvals are deliberately not here: they have the Approval Desk.
 *
 * The panel renders in a portal on document.body, anchored under the bell, so
 * no page's stacking (sticky table headers, the top bar's own z-index) can
 * cover or clip it.
 *
 * Every href comes from the server and is still passed through
 * safeContentHref, and navigation is in-app (navigateToHref), never a reload.
 */

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../shared/LoadingSpinner';
import Button from '../shared/Button';
import CloseButton from '../shared/CloseButton';
import { useCurrentUser } from '../../contexts/UserContext';
import { useActionItems, type ActionItem, type ActionSection } from '../../hooks/useActionItems';
import { navigateToHref } from '../../utils/navigation';
import { safeContentHref } from '../home/safeUrl';
import { CHROME_CONTROL_CLASS } from '../shared/chromeControl';
import { ageDays } from '../home/age';
import { AgeChip } from '../shared/ApprovalQueue';
import { TEXT } from '../shared/Typography';

export const COUNT_CAP = 99;

export function formatCount(n: number): string {
  return n > COUNT_CAP ? `${COUNT_CAP}+` : String(n);
}

/* ── "new since you last looked" ─────────────────────────────────────── */

const lastSeenKey = (who: string) => `gra.actionItems.lastSeen.${who}`;

function readLastSeen(who: string): number | null {
  try {
    const v = window.localStorage.getItem(lastSeenKey(who));
    const n = v ? Number(v) : NaN;
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeLastSeen(who: string, at: number): void {
  try {
    window.localStorage.setItem(lastSeenKey(who), String(at));
  } catch {
    // Storage unavailable: the dot simply shows again next time.
  }
}

/**
 * When the newest outstanding thing arrived. `section.newestAt` covers the
 * whole section; the five items sent are only a sample of it, so they are the
 * fallback for a server that does not send it yet.
 */
function newestItemTime(sections: ActionSection[]): number {
  let newest = 0;
  const consider = (v?: string) => {
    const t = v ? new Date(v).getTime() : NaN;
    if (Number.isFinite(t) && t > newest) newest = t;
  };
  for (const s of sections) {
    if (s.newestAt) consider(s.newestAt);
    else s.items.forEach(i => consider(i.createdAt));
  }
  return newest;
}

/* ── panel pieces ────────────────────────────────────────────────────── */

const SEVERITY_ACCENT: Record<NonNullable<ActionItem['severity']>, string> = {
  normal: 'border-l-transparent',
  warning: 'border-l-amber-600',
  overdue: 'border-l-red-600',
};

function ItemRow({ item, onNavigate }: { item: ActionItem; onNavigate: (href: string) => void }) {
  const href = safeContentHref(item.href);
  const days = item.createdAt ? ageDays(item.createdAt) : null;
  const accent = SEVERITY_ACCENT[item.severity || 'normal'];

  const body = (
    <>
      <span className="min-w-0 flex-1">
        <span
          className={`block break-words line-clamp-2 ${TEXT.bodyStrong}`}
          title={item.title}
        >
          {item.title}
        </span>
        {item.subtitle && (
          <span
            className={`mt-0.5 block break-words line-clamp-2 ${TEXT.bodyMuted}`}
            title={item.subtitle}
          >
            {item.subtitle}
          </span>
        )}
      </span>
      {days !== null && <AgeChip days={days} />}
    </>
  );

  const rowCls = `flex items-start gap-3 border-l-2 ${accent} px-4 py-2.5`;

  // An href that fails the check is shown but not followed.
  if (!href) return <li className={rowCls}>{body}</li>;
  return (
    <li>
      <a
        href={href}
        onClick={e => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
          e.preventDefault();
          onNavigate(href);
        }}
        className={`${rowCls} transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-600`}
      >
        {body}
      </a>
    </li>
  );
}

function Section({
  section,
  onNavigate,
  onRetry,
}: {
  section: ActionSection;
  onNavigate: (href: string) => void;
  onRetry: () => void;
}) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="py-2">
      <div className="flex items-center justify-between px-4 pb-1">
        <h4 id={headingId} className={TEXT.bodyEmphasis}>
          {section.label}
        </h4>
        {!section.error && (
          <span className="rounded-full bg-gray-100 px-2 font-mono text-xs font-semibold tabular-nums text-gray-700">
            {formatCount(section.count)}
          </span>
        )}
      </div>

      {section.error ? (
        <div className="flex items-center justify-between gap-3 px-4 py-1">
          <p className={TEXT.bodyMuted}>Couldn&apos;t load {section.label}</p>
          <Button variant="outline" size="xs" onClick={onRetry}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          <ul>
            {section.items.map(item => (
              <ItemRow key={item.id} item={item} onNavigate={onNavigate} />
            ))}
          </ul>
          {section.count > section.items.length && (
            <p className={`px-4 pt-1 ${TEXT.caption}`}>
              and {section.count - section.items.length} more
            </p>
          )}
        </>
      )}
    </section>
  );
}

/* ── the bell ────────────────────────────────────────────────────────── */

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== 'undefined' &&
      !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
  );
  useEffect(() => {
    const mql = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mql?.addEventListener) return undefined;
    const on = (e: MediaQueryListEvent) => setReduced(e.matches);
    mql.addEventListener('change', on);
    return () => mql.removeEventListener('change', on);
  }, []);
  return reduced;
}

/** Below Tailwind's `sm` the panel is a full-width sheet with a scrim. */
function useIsNarrow(): boolean {
  const query = '(max-width: 639px)';
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.(query)?.matches
  );
  useEffect(() => {
    const mql = window.matchMedia?.(query);
    if (!mql?.addEventListener) return undefined;
    const on = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mql.addEventListener('change', on);
    return () => mql.removeEventListener('change', on);
  }, []);
  return narrow;
}

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function NotificationBell({
  token,
  onNavigate,
}: {
  token: string | null | undefined;
  /** Called before any in-app navigation — App ends its landing hold here. */
  onNavigate?: () => void;
}) {
  const user = useCurrentUser();
  const who = user?.userId || user?.email || 'anonymous';
  const { data, loading, error, refresh } = useActionItems(token, user?.userId || user?.email);
  const reducedMotion = usePrefersReducedMotion();
  const isSheet = useIsNarrow();

  const [open, setOpen] = useState(false);
  const [lastSeen, setLastSeen] = useState<number | null>(() => readLastSeen(who));
  const [bump, setBump] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const titleId = useId();
  // Where the desktop dropdown sits: under the bell, right edges aligned.
  const [anchor, setAnchor] = useState<CSSProperties>({});

  const total = data?.total ?? 0;
  const sections = useMemo(() => data?.sections ?? [], [data]);
  const hasErrorSection = sections.some(s => s.error);

  // Re-read when the person changes, so one user's "seen" never hides another's dot.
  useEffect(() => setLastSeen(readLastSeen(who)), [who]);

  const newest = newestItemTime(sections);
  const hasNew = total > 0 && (lastSeen === null || newest > lastSeen);

  // A gentle pulse when the count changes — not on first load, not under reduced motion.
  const prevTotal = useRef<number | null>(null);
  useEffect(() => {
    if (!data) return undefined;
    const prev = prevTotal.current;
    prevTotal.current = total;
    if (prev === null || prev === total || reducedMotion) return undefined;
    setBump(true);
    const t = setTimeout(() => setBump(false), 200);
    return () => clearTimeout(t);
  }, [data, total, reducedMotion]);

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) bellRef.current?.focus();
  }, []);

  const openPanel = () => {
    setOpen(true);
    // Opening is "looking"; it clears the dot and nothing else.
    const now = Date.now();
    writeLastSeen(who, now);
    setLastSeen(now);
    void refresh();
  };

  // Anchor the dropdown to the bell, and keep it there as the window resizes.
  useLayoutEffect(() => {
    if (!open || isSheet) return undefined;
    const place = () => {
      const r = bellRef.current?.getBoundingClientRect();
      if (r) setAnchor({ top: r.bottom + 8, right: Math.max(12, window.innerWidth - r.right) });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open, isSheet]);

  // Focus into the panel when it opens.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  // Click outside closes: outside both the bell and the (portalled) panel.
  // Focus goes back to the bell unless the click landed on something else
  // focusable, which the person evidently chose.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!wrapperRef.current?.contains(t) && !panelRef.current?.contains(t)) close(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  const onPanelKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
      return;
    }
    if (e.key !== 'Tab' || !panelRef.current) return;
    // Keep Tab inside the panel while it is open.
    const nodes = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panelRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const go = (href: string) => {
    close();
    onNavigate?.();
    navigateToHref(href);
  };

  let body: ReactNode;
  if (!data && loading) {
    body = (
      <div className="py-6">
        <LoadingSpinner size="sm" message="Loading your items..." />
      </div>
    );
  } else if (!data && error) {
    body = (
      <div className="flex items-center justify-between gap-3 px-4 py-6">
        <p className={TEXT.bodyMuted}>Couldn&apos;t load your notifications</p>
        <Button variant="outline" size="xs" onClick={() => void refresh()}>
          Retry
        </Button>
      </div>
    );
  } else if (total === 0 && !hasErrorSection) {
    body = (
      <div className="px-4 py-8 text-center">
        <p className={TEXT.emphasis}>You&apos;re all clear</p>
        <p className={`mt-1 ${TEXT.bodyMuted}`}>Nothing is waiting on you right now.</p>
      </div>
    );
  } else {
    body = (
      <div className="divide-y divide-gray-100">
        {sections.map(s => (
          <Section key={s.key} section={s} onNavigate={go} onRetry={() => void refresh()} />
        ))}
      </div>
    );
  }

  const countText = formatCount(total);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        ref={bellRef}
        type="button"
        onClick={() => (open ? close() : openPanel())}
        aria-label={`Notifications: ${total} outstanding`}
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="dialog"
        // Same open styling as the profile menu, via CHROME_CONTROL_CLASS's data-[open].
        data-open={open ? '' : undefined}
        className={`relative inline-flex h-11 w-11 items-center justify-center rounded-full ${CHROME_CONTROL_CLASS}`}
      >
        <BellIcon className="h-5 w-5" aria-hidden />
        {total > 0 && (
          <span
            data-testid="action-items-count"
            aria-hidden
            className={`absolute -right-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-white px-1 font-mono text-2xs font-semibold tabular-nums text-chrome-700 ring-1 ring-white/70 ring-offset-1 ring-offset-chrome-700 transition-transform duration-200 ease-in-out motion-reduce:transition-none ${
              bump ? 'scale-125' : 'scale-100'
            }`}
          >
            {countText}
          </span>
        )}
        {hasNew && (
          <span
            data-testid="action-items-new"
            aria-hidden
            className="absolute left-1.5 top-1.5 h-2 w-2 rounded-full bg-amber-400 ring-2 ring-chrome-700"
          />
        )}
      </button>

      {open &&
        createPortal(
          <>
            {/* Mobile: a scrim behind the sheet, which also closes it. */}
            <div
              className="fixed inset-0 z-[90] bg-gray-900/30 sm:hidden"
              aria-hidden
              onClick={() => close()}
            />
            <div
              ref={panelRef}
              id={panelId}
              role="dialog"
              // Modal only as the mobile sheet, where a scrim blocks the page.
              aria-modal={isSheet}
              aria-labelledby={titleId}
              tabIndex={-1}
              onKeyDown={onPanelKeyDown}
              style={isSheet ? undefined : anchor}
              className="fixed inset-x-0 top-0 z-[100] flex max-h-[85vh] flex-col rounded-b-lg border-b border-gray-200 bg-white pt-safe-mobile shadow-xl focus:outline-none sm:inset-x-auto sm:max-h-[75vh] sm:w-[32rem] sm:max-w-[calc(100vw-1.5rem)] sm:rounded-lg sm:border sm:pt-0 sm:shadow-md"
            >
              <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
                <h3 id={titleId} className={TEXT.emphasis}>
                  Notifications
                  {total > 0 && (
                    <span className={`ml-2 font-mono tabular-nums ${TEXT.helper}`}>
                      {countText}
                    </span>
                  )}
                </h3>
                <CloseButton onClick={() => close()} size="sm" label="Close notifications" />
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto">{body}</div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}
